/**
 * HTTP API utilities for authentication
 */
import config from '../config';
import { generateCorrelationId } from './api';
import { debugLog, debugError } from './debug';

/**
 * Send login request to HTTP API
 * @param {string} email - User email address
 * @param {string} captchaToken - Cloudflare Turnstile captcha token (optional)
 * @returns {Promise<object>} - API response
 */
export async function sendLoginRequest(email, captchaToken = null) {
  const url = `${config.AVIOTRACK_API_URL}/AuthenticationService/login`;

  const requestBody = {
    header: {
      clientId: config.CLIENT_ID,
      service: 'AuthenticationService',
      function: 'login'
    },
    loginType: 'PASSWORDLESS',
    email
  };

  // Add captcha token if provided
  if (captchaToken) {
    requestBody.captchaToken = captchaToken;
  }

  debugLog('🔐 Sending login request to:', url);
  debugLog('📦 Request body:', requestBody);
  debugLog('🔑 Authorization header:', `Bearer ${config.CLIENT_ID}`);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.CLIENT_ID}`
      },
      body: JSON.stringify(requestBody)
    });

    debugLog('📨 Response status:', response.status, response.statusText);
    debugLog('📨 Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      debugError('❌ Error response body:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    debugLog('✅ Login response data:', data);
    return data;
  } catch (error) {
    debugError('❌ Login request failed:', error);
    throw error;
  }
}

/**
 * Send email verification request (for new user registration)
 * @param {string} email - User email address
 * @param {string} code - Verification code from email
 * @returns {Promise<object>} - API response with JWT token
 */
export async function sendEmailVerificationRequest(email, code) {
  const url = `${config.AVIOTRACK_API_URL}/UserAccountService/verifyEmail`;

  const requestBody = {
    header: {
      clientId: config.CLIENT_ID,
      service: 'UserAccountService',
      function: 'verifyEmail'
    },
    email,
    verificationCode: code
  };

  debugLog('✉️ Sending email verification request to:', url);
  debugLog('📦 Request body:', requestBody);
  debugLog('🔑 Authorization header:', `Bearer ${config.CLIENT_ID}`);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.CLIENT_ID}`
      },
      body: JSON.stringify(requestBody)
    });

    debugLog('📨 Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      debugError('❌ Error response body:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    debugLog('✅ Email verification response data:', data);
    return data;
  } catch (error) {
    debugError('❌ Email verification request failed:', error);
    throw error;
  }
}

/**
 * Send validation (code verification) request to HTTP API for passwordless login
 * @param {string} email - User email address
 * @param {string} code - Verification code
 * @returns {Promise<object>} - API response with JWT token
 */
export async function sendAuthorizationRequest(email, code) {
  const url = `${config.AVIOTRACK_API_URL}/AuthenticationService/validate`;

  const requestBody = {
    header: {
      clientId: config.CLIENT_ID,
      service: 'AuthenticationService',
      function: 'validate'
    },
    email,
    validationCode: code
  };

  debugLog('🔑 Sending validation request to:', url);
  debugLog('📦 Request body:', requestBody);
  debugLog('🔑 Authorization header:', `Bearer ${config.CLIENT_ID}`);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.CLIENT_ID}`
      },
      body: JSON.stringify(requestBody)
    });

    debugLog('📨 Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      debugError('❌ Error response body:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    debugLog('✅ Validation response data:', data);
    return data;
  } catch (error) {
    debugError('❌ Validation request failed:', error);
    throw error;
  }
}

/**
 * Send registration request to HTTP API
 * @param {object} registrationData - User registration data
 * @param {string} captchaToken - Cloudflare Turnstile captcha token (optional)
 * @returns {Promise<object>} - API response
 */
export async function sendRegistrationRequest(registrationData, captchaToken = null) {
  const url = `${config.AVIOTRACK_API_URL}/UserAccountService/registerUser`;

  const requestBody = {
    header: {
      clientId: config.CLIENT_ID,
      service: 'UserAccountService',
      function: 'registerUser'
    },
    ...registrationData
  };

  // Add captcha token if provided
  if (captchaToken) {
    requestBody.captchaToken = captchaToken;
  }

  debugLog('📝 Sending registration request to:', url);
  debugLog('📦 Request body:', requestBody);
  debugLog('🔑 Authorization header:', `Bearer ${config.CLIENT_ID}`);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.CLIENT_ID}`
      },
      body: JSON.stringify(requestBody)
    });

    debugLog('📨 Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      debugError('❌ Error response body:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    debugLog('✅ Registration response data:', data);
    return data;
  } catch (error) {
    debugError('❌ Registration request failed:', error);
    throw error;
  }
}

/**
 * Verify JWT token validity
 * @param {string} token - JWT token
 * @returns {Promise<boolean>} - True if token is valid
 */
export async function verifyToken(token) {
  const url = `${config.AVIOTRACK_API_URL}/AuthenticationService/verifyToken`;

  const requestBody = {
    header: {
      clientId: config.CLIENT_ID,
      service: 'AuthenticationService',
      function: 'verifyToken'
    },
    token: `Bearer ${token}`
  };

  debugLog('✓ Sending token verification request to:', url);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.CLIENT_ID}`
      },
      body: JSON.stringify(requestBody)
    });

    debugLog('📨 Verification response status:', response.status);

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    debugLog('✓ Token verification result:', data.header?.apiStatus === 'OK');
    return data.header?.apiStatus === 'OK';
  } catch (error) {
    debugError('❌ Token verification failed:', error);
    return false;
  }
}

/**
 * Refresh access token using refresh token
 * @param {string} refreshToken - Refresh token
 * @returns {Promise<object|null>} - New token data or null on failure
 */
export async function refreshAccessToken(refreshToken) {
  const url = `${config.AVIOTRACK_API_URL}/AuthenticationService/refresh`;

  // The API rejects a request with no client credentials (HTTP 401) even for
  // anonymous endpoints, so refresh MUST carry the clientId the same way every
  // other anonymous call does. Without them every refresh 401'd, which made
  // refreshAndPersistTokens return null and forceLogoutAndRedirect bounce the
  // user to the login screen. That was invisible on the email-code flow — the
  // stored expiry keeps refresh from running until the token's final hour — but
  // fired instantly on passkey login, whose response carries no expiry at all.
  const requestBody = {
    header: {
      clientId: config.CLIENT_ID,
      apiKey: config.CLIENT_ID,
      service: 'AuthenticationService',
      function: 'refresh'
    },
    refreshToken
  };

  debugLog('🔄 Sending token refresh request to:', url);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.CLIENT_ID}`
      },
      body: JSON.stringify(requestBody)
    });

    debugLog('📨 Refresh response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      debugError('❌ Refresh error response:', errorText);
      return null;
    }

    const data = await response.json();

    if (data.header?.apiStatus === 'OK' && data.accessToken) {
      debugLog('✅ Token refreshed successfully');
      return {
        accessToken: data.accessToken,
        // Backend now reissues a refresh token on every refresh so the user's
        // 7-day window slides forward with activity. Older backend revisions
        // may omit this — null falls through to "keep the existing refresh
        // token" in the caller.
        refreshToken: data.refreshToken || null,
        expiryTimeUTC: data.expiryTimeUTC,
        userId: data.userId,
        username: data.username
      };
    } else {
      debugError('❌ Refresh failed:', data.header?.errors);
      return null;
    }
  } catch (error) {
    debugError('❌ Token refresh request failed:', error);
    return null;
  }
}

// ----------------------------------------------------------------------------
// Shared single-flight token refresh
//
// Both the HTTP layer (sendAuthenticatedRequest, on 401) and the WebSocket
// hook trigger a refresh. Without a shared in-flight promise, a burst of
// concurrent 401s would fire N parallel refreshes, each consuming the
// previous one's refresh token (now that the backend rotates it) and most
// of them failing. This singleton ensures at most one refresh is in flight
// at a time and all callers await the same result.
//
// Persists the new tokens directly into localStorage so callers don't have
// to thread the response through their own state.
let _refreshInFlight = null;

/**
 * Refresh the access token and persist the new tokens. Returns the new
 * access token on success, or null if refresh failed (caller should treat
 * this as "force logout"). Concurrent calls share a single network round
 * trip.
 */
export async function refreshAndPersistTokens() {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      const currentRefreshToken = localStorage.getItem('aviotrack_refresh_token');
      if (!currentRefreshToken) {
        debugError('❌ refreshAndPersistTokens: no refresh token in storage');
        return null;
      }
      const result = await refreshAccessToken(currentRefreshToken);
      if (!result || !result.accessToken) return null;

      localStorage.setItem('aviotrack_access_token', result.accessToken);
      // Rotate the refresh token when the backend returns a fresh one,
      // otherwise keep the existing one (older backend, identity refresh).
      if (result.refreshToken) {
        localStorage.setItem('aviotrack_refresh_token', result.refreshToken);
      }
      if (result.expiryTimeUTC) {
        localStorage.setItem('aviotrack_token_expiry', result.expiryTimeUTC);
      }
      return result.accessToken;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
}

/**
 * Generic HTTP API request helper with JWT authentication
 * @param {string} service - Service name
 * @param {string} func - Function name
 * @param {object} data - Request data
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - API response
 */
async function sendAuthenticatedRequest(service, func, data, token) {
  // The refresh endpoint itself must never recurse through the 401 path —
  // a 401 from /refresh means the refresh token itself is dead and the user
  // has to log in again. Same goes for the unauthenticated login endpoints.
  const isAuthFlow = service === 'AuthenticationService' &&
    (func === 'refresh' || func === 'login' || func === 'oauthLogin');

  // EXPIRY IS CHECKED LOCALLY, BEFORE SENDING — the 401 path below cannot see it.
  //
  // The backend does NOT return 401 for a dead token on data endpoints. Verified
  // 2026-09-08 against AircraftTrackingService.getMaxDistancePoints: a bad-signature
  // token AND no token at all both return HTTP 200 with apiStatus "OK", no errors,
  // and totalRecords 0. An expired session is therefore indistinguishable from
  // "you have no data", so `firstResponse.status !== 401` always holds, refresh
  // never runs, and nothing ever logs out. One tab sat on a token that died at
  // 07:23 UTC and made 871 identical rejected calls over the next six hours while
  // the statistics page just rendered empty.
  //
  // isTokenKnownExpired (not isTokenExpired) so a missing expiry — passkey logins
  // store none — is treated as "unknown", never as grounds for a forced logout.
  if (!isAuthFlow) {
    const auth = await import('./auth.js');
    if (auth.isTokenKnownExpired()) {
      debugLog(`🔒 ${service}.${func}: access token has expired — refreshing before send`);
      const refreshed = await refreshAndPersistTokens();
      if (!refreshed) {
        debugError(`❌ ${service}.${func}: token expired and refresh failed — forcing logout`);
        auth.forceLogoutAndRedirect('access token expired and refresh failed');
        throw new Error('Session expired');
      }
      token = refreshed;
    }
  }

  // First attempt with the supplied token. If we get a 401, try a single
  // shared refresh, then retry once with the new token. If THAT fails (or
  // refresh itself fails), force logout — the FE used to silently keep
  // looping on broken requests instead.
  const firstResponse = await doFetch(service, func, data, token);
  if (firstResponse.status !== 401 || isAuthFlow) {
    return parseOrThrow(firstResponse, service, func);
  }

  debugLog(`🔁 ${service}.${func} returned 401 — attempting token refresh`);
  // Lazy-import to avoid a circular dep at module load (auth.js imports nothing,
  // but it would be reasonable for it to). We already have refresh helpers
  // colocated here, so just call them directly.
  const newToken = await refreshAndPersistTokens();
  if (!newToken) {
    debugError(`❌ ${service}.${func}: refresh failed, forcing logout`);
    // Lazy require so this module stays usable in tests that don't stub auth.
    const auth = await import('./auth.js');
    auth.forceLogoutAndRedirect(`401 on ${service}.${func} and refresh failed`);
    throw new Error(`HTTP error! status: 401 (refresh failed)`);
  }

  const retryResponse = await doFetch(service, func, data, newToken);
  if (retryResponse.status === 401) {
    debugError(`❌ ${service}.${func}: still 401 after refresh, forcing logout`);
    const auth = await import('./auth.js');
    auth.forceLogoutAndRedirect(`401 on ${service}.${func} even after refresh`);
    throw new Error(`HTTP error! status: 401 (after refresh)`);
  }
  return parseOrThrow(retryResponse, service, func);
}

/**
 * Unauthenticated request for public endpoints (no token, no refresh, no
 * forced logout). Deliberately bypasses sendAuthenticatedRequest: its
 * isTokenKnownExpired → forceLogoutAndRedirect path would bounce logged-out
 * users straight back to the login page in a loop.
 * @param {string} service - Service name
 * @param {string} func - Function name
 * @param {object} data - Request data
 * @returns {Promise<object>} - API response
 */
export async function sendPublicRequest(service, func, data = {}) {
  const response = await doFetch(service, func, data, null);
  return parseOrThrow(response, service, func);
}

/** Build the standard request body + send it, returning the raw Response. */
async function doFetch(service, func, data, token) {
  const url = `${config.AVIOTRACK_API_URL}/${service}/${func}`;
  const requestBody = {
    header: {
      token: token,
      apiKey: config.CLIENT_ID,
      service: service,
      function: func,
      correlationID: generateCorrelationId()
    },
    ...data
  };

  debugLog(`→ HTTP ${service}.${func} to:`, url);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.CLIENT_ID}`
      },
      body: JSON.stringify(requestBody)
    });
    debugLog(`← Response status:`, response.status, response.statusText);
    return response;
  } catch (networkError) {
    debugError(`❌ ${service}.${func} network error:`, networkError);
    debugError(`❌ Failed request body:`, JSON.stringify(requestBody, null, 2));
    throw networkError;
  }
}

/** Parse a Response that came back !=401 — preserve the prior error shape. */
async function parseOrThrow(response, service, func) {
  if (!response.ok) {
    const errorText = await response.text();
    debugError(`❌ ${service}.${func} HTTP ${response.status} error response:`, errorText);
    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
  }
  const result = await response.json();
  debugLog(`← HTTP ${service}.${func} response:`, result);
  return result;
}

// ============================================================================
// User Account Service
// ============================================================================

/**
 * Get account information including profile, receivers, and settings
 * @param {string} email - User email
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Account data
 */
export async function getAccountInfo(email, token) {
  return sendAuthenticatedRequest('UserAccountService', 'getAccountInfo', { email }, token);
}

// ============================================================================
// Airport Service
// ============================================================================

/**
 * Get airports within map bounds
 * @param {object} bounds - Map bounds {topLat, bottomLat, leftLon, rightLon}
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Airports data
 */
export async function getAirportsInBounds(bounds, token) {
  return sendAuthenticatedRequest('AirportService', 'getAirportsInBounds', bounds, token);
}

/**
 * Get nearest airport to coordinates
 * @param {number} latitude - Latitude in decimal degrees
 * @param {number} longitude - Longitude in decimal degrees
 * @param {string} token - JWT access token
 * @param {number} maxDistanceKm - Maximum search radius in km (optional, default 50)
 * @returns {Promise<object>} - Nearest airport data
 */
export async function getNearestAirport(latitude, longitude, token, maxDistanceKm = 50) {
  return sendAuthenticatedRequest('AirportService', 'getNearestAirport', {
    latitude,
    longitude,
    maxDistanceKm
  }, token);
}

/**
 * Search for airports by name, city, ICAO, or IATA code
 * @param {string} query - Search query (e.g., "KJFK", "London", "LHR")
 * @param {string} token - JWT access token
 * @param {number} limit - Maximum results to return (optional, default 10)
 * @returns {Promise<object>} - Search results with airports array and totalCount
 */
export async function searchAirports(query, token, limit = 10) {
  return sendAuthenticatedRequest('AirportService', 'searchAirports', {
    query,
    limit
  }, token);
}

/**
 * Get airport features (runways, taxiways, aprons, etc.) from OpenStreetMap data
 * @param {string} icaoCode - Airport ICAO code (e.g., "EGLL", "KJFK")
 * @param {string} token - JWT access token
 * @param {number} radiusKm - Search radius in km (optional, default 5)
 * @returns {Promise<object>} - Airport features data including runways, taxiways, aprons, terminals, parkingPositions
 */
export async function getAirportFeatures(icaoCode, token, radiusKm = 5) {
  const response = await sendAuthenticatedRequest('AirportService', 'getAirportFeatures', {
    icaoCode,
    radiusKm
  }, token);

  // Transform backend response to frontend format
  if (response && (response.header?.apiStatus === 'OK' || response.header?.apiStatus === 'SUCCESS')) {
    response.features = transformAirportFeatures(response);
  }

  return response;
}

/**
 * Transform backend airport features to frontend format
 * Backend returns geometry as [lon, lat] pairs, frontend expects [lat, lng]
 */
function transformAirportFeatures(response) {
  const transformFeature = (feature) => {
    if (!feature) return null;

    // Convert geometry from [lon, lat] to [lat, lng] for Leaflet
    let coordinates = null;
    if (feature.geometry && Array.isArray(feature.geometry)) {
      coordinates = feature.geometry.map(coord => [coord[1], coord[0]]); // Swap lon/lat to lat/lng
    } else if (feature.latitude != null && feature.longitude != null) {
      coordinates = [[feature.latitude, feature.longitude]];
    }

    return {
      id: feature.osmId,
      osmId: feature.osmId,
      osmType: feature.osmType,
      name: feature.name,
      ref: feature.ref,
      surface: feature.surface,
      length: feature.lengthMeters,
      width: feature.widthMeters,
      latitude: feature.latitude,
      longitude: feature.longitude,
      coordinates: coordinates,
      lighted: feature.lighted,
      heading: feature.heading,
      tags: feature.tags
    };
  };

  const transformArray = (arr) => {
    if (!arr || !Array.isArray(arr)) return [];
    return arr.map(transformFeature).filter(f => f !== null);
  };

  return {
    runways: transformArray(response.runways),
    taxiways: transformArray(response.taxiways),
    terminals: transformArray(response.terminals),
    parkingPositions: transformArray(response.parkingPositions),
    aprons: transformArray(response.aprons),
    hangars: transformArray(response.hangars),
    navaids: transformArray(response.navaids),
    tower: response.tower ? transformFeature(response.tower) : null,
    totalFeatures: response.totalFeatures
  };
}

// ============================================================================
// Airport Feature Management (Admin only for write operations)
// ============================================================================

/**
 * Sync airport features from OSM to local database (admin only)
 * @param {string} icaoCode - Airport ICAO code
 * @param {string} token - JWT access token
 * @param {number} radiusKm - Search radius in km (optional, default 5)
 * @param {boolean} forceFullSync - Force full resync, deleting non-modified features first (optional)
 * @returns {Promise<object>} - Sync result with counts (inserted, updated, deleted, conflicts)
 */
export async function syncAirportFeaturesFromOsm(icaoCode, token, radiusKm = 5, forceFullSync = false) {
  return sendAuthenticatedRequest('AirportService', 'syncAirportFeaturesFromOsm', {
    icaoCode,
    radiusKm,
    forceFullSync
  }, token);
}

/**
 * Save (create or update) an airport feature (admin only)
 * @param {object} feature - Airport feature to save
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Saved feature with action (CREATED or UPDATED)
 */
export async function saveAirportFeature(feature, token) {
  return sendAuthenticatedRequest('AirportService', 'saveAirportFeature', {
    feature
  }, token);
}

/**
 * Delete an airport feature (admin only)
 * @param {number} featureId - Feature ID to delete
 * @param {string} airportIcao - Airport ICAO code
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Delete result
 */
export async function deleteAirportFeature(featureId, airportIcao, token) {
  return sendAuthenticatedRequest('AirportService', 'deleteAirportFeature', {
    featureId,
    airportIcao
  }, token);
}

/**
 * Detect conflicts between local features and OSM data (admin only)
 * @param {string} icaoCode - Airport ICAO code
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Conflicts list
 */
export async function detectAirportFeatureConflicts(icaoCode, token) {
  return sendAuthenticatedRequest('AirportService', 'detectAirportFeatureConflicts', {
    icaoCode
  }, token);
}

/**
 * Resolve a feature conflict by choosing local or OSM version (admin only)
 * @param {number} featureId - Feature ID with conflict
 * @param {string} resolution - Resolution choice: "KEEP_LOCAL" or "USE_OSM"
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Resolved feature
 */
export async function resolveAirportFeatureConflict(featureId, resolution, token) {
  return sendAuthenticatedRequest('AirportService', 'resolveAirportFeatureConflict', {
    featureId,
    resolution
  }, token);
}

// ============================================================================
// Aircraft Tracking Service
// ============================================================================

/**
 * Get track points for an aircraft
 * @param {string} trackId - Track ID
 * @param {number} intervalSeconds - Sample interval in seconds
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Track points data
 */
export async function getTrackPoints(trackId, intervalSeconds, token) {
  return sendAuthenticatedRequest('AircraftTrackingService', 'getTrackPoints', {
    trackId,
    intervalSeconds
  }, token);
}

/**
 * Track points and aircraft details (with photo) for a hex over a time window, positions merged
 * from every receiver. The server defaults the window to the last 30 min and caps it at 6 h,
 * keeping the end; an empty `points` with OK status means the positions have aged out.
 * @param {object} q - { icao24, startTime?, endTime?, callsign?, intervalSeconds? }; times are the
 *   server's LocalDateTime (UTC, no zone), see landingTrack.toServerDateTime
 * @param {string} token - JWT access token
 */
export async function getTrackPointsByHex({ icao24, startTime, endTime, callsign, intervalSeconds }, token) {
  const requestData = { icao24 };
  if (startTime) requestData.startTime = startTime;
  if (endTime) requestData.endTime = endTime;
  if (callsign) requestData.callsign = callsign;
  if (intervalSeconds) requestData.intervalSeconds = intervalSeconds;
  return sendAuthenticatedRequest('AircraftTrackingService', 'getTrackPoints', requestData, token);
}

/**
 * Get list of tracked aircraft for a receiver on a specific date
 * @param {string} receiverUuid - Receiver UUID
 * @param {string} date - Date in YYYY-MM-DD format (defaults to today)
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Tracked aircraft list
 */
export async function getTrackedAircraftList(receiverUuid, date, token) {
  const requestData = { receiverUuid };
  if (date) {
    requestData.date = date;
  }
  return sendAuthenticatedRequest('AircraftTrackingService', 'getTrackedAircraftList', requestData, token);
}

/**
 * Get a one-time snapshot of the aircraft a receiver is currently tracking.
 *
 * Called when the user switches to a receiver. The WebSocket only streams incremental
 * aircraft_update messages, so on a fresh switch aircraft that already hold a validated
 * position show blue (unverified) until their next position frame arrives. This snapshot
 * returns the current per-receiver validated state (entries in the same shape as
 * aircraft_update) so already-verified aircraft render verified immediately.
 *
 * @param {string} receiverUuid - Receiver UUID
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - { receiverUuid, aircraft: [...], count }
 */
export async function getReceiverSnapshot(receiverUuid, token) {
  return sendAuthenticatedRequest('BeastTrackingService', 'getReceiverSnapshot', { receiverUuid }, token);
}

// ===========================================================================
// WebAuthn / passkeys
// ===========================================================================

/** Anonymous POST for the pre-login passkey ceremony steps. */
async function sendAnonymousRequest(service, func, data) {
  const url = `${config.AVIOTRACK_API_URL}/${service}/${func}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config.CLIENT_ID}`
    },
    body: JSON.stringify({
      header: { clientId: config.CLIENT_ID, apiKey: config.CLIENT_ID, service, function: func },
      ...data
    })
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP error! status: ${response.status} - ${text}`);
  }
  return response.json();
}

/**
 * Start a passkey login ceremony (anonymous). With an email the server lists
 * that account's credentials; without one the resident-key flow applies.
 */
export async function beginPasskeyAssertion(email = null) {
  return sendAnonymousRequest('WebAuthnService', 'beginAssertion',
    email ? { username: email } : {});
}

/**
 * Finish a passkey login (anonymous): the app's Phase-3 endpoint verifies the
 * assertion AND mints AvioTrack's access + sliding refresh tokens with roles.
 * Response shape matches the email-code authorise flow.
 */
export async function finishPasskeyLogin(assertionPayload) {
  return sendAnonymousRequest('AuthenticationService', 'passkeyLogin', assertionPayload);
}

/** Start passkey registration for the logged-in account. The framework
 * requires the credential's friendly name at BEGIN (carried through the
 * ceremony), not just at finish. */
export async function beginPasskeyRegistration(token, name) {
  return sendAuthenticatedRequest('WebAuthnService', 'beginRegistration', { name }, token);
}

/** Finish passkey registration (attestation payload + friendly name). */
export async function finishPasskeyRegistration(attestationPayload, token) {
  return sendAuthenticatedRequest('WebAuthnService', 'finishRegistration', attestationPayload, token);
}

/** List the logged-in account's registered passkeys. */
export async function listPasskeys(token) {
  return sendAuthenticatedRequest('WebAuthnService', 'listMyCredentials', {}, token);
}

/** Revoke one of the logged-in account's passkeys by credential id (hex). */
export async function revokePasskey(credentialIdHex, token) {
  return sendAuthenticatedRequest('WebAuthnService', 'revokeMyCredential', { credentialIdHex }, token);
}

/**
 * Get tracks for a specific aircraft on a receiver for a specific date
 * @param {string} icao24 - ICAO 24-bit address
 * @param {string} receiverUuid - Receiver UUID
 * @param {string} date - Date in YYYY-MM-DD format (defaults to today)
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Aircraft tracks
 */
export async function getAircraftTracks(icao24, receiverUuid, date, token) {
  const requestData = { icao24, receiverUuid };
  if (date) {
    requestData.date = date;
  }
  return sendAuthenticatedRequest('AircraftTrackingService', 'getAircraftTracks', requestData, token);
}

/**
 * Get max distance (furthest) points for a receiver on a specific date
 * @param {string} receiverUuid - Receiver UUID
 * @param {string} date - Date in YYYY-MM-DD format (optional, defaults to today)
 * @param {boolean} consolidated - If true, returns one record per aircraft (default true)
 * @param {boolean} allTime - If true, returns all-time records instead of daily (default false)
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Max distance points data
 */
export async function getMaxDistancePoints(receiverUuid, date, consolidated, allTime, token) {
  const requestData = { receiverUuid };
  if (date) {
    requestData.date = date;
  }
  if (typeof consolidated === 'boolean') {
    requestData.consolidated = consolidated;
  }
  if (typeof allTime === 'boolean') {
    requestData.allTime = allTime;
  }
  return sendAuthenticatedRequest('AircraftTrackingService', 'getMaxDistancePoints', requestData, token);
}

/**
 * Reset max distance outline records
 * @param {string} receiverUuid - The receiver UUID
 * @param {boolean} allTime - If true, reset all-time records; if false, reset daily records
 * @param {string} date - Optional date for daily reset (defaults to today)
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Reset result with recordsDeleted count
 */
export async function resetMaxDistanceOutline(receiverUuid, allTime, date, token) {
  const requestData = { receiverUuid };
  if (typeof allTime === 'boolean') {
    requestData.allTime = allTime;
  }
  if (date && !allTime) {
    requestData.date = date;
  }
  return sendAuthenticatedRequest('AircraftTrackingService', 'resetMaxDistanceOutline', requestData, token);
}

/**
 * Get receiver heatmap data for a specific date
 * Returns position frequency by sector (15 degrees bearing x 50km distance)
 * @param {string} receiverUuid - Receiver UUID
 * @param {string} date - Date in YYYY-MM-DD format (optional, defaults to today)
 * @param {string} unitSystem - Unit system: 'metric', 'imperial', or 'aeronautical'
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Heatmap sectors data
 */
export async function getReceiverHeatmap(receiverUuid, date, unitSystem, token) {
  const requestData = { receiverUuid };
  if (date) {
    requestData.date = date;
  }
  if (unitSystem) {
    requestData.unitSystem = unitSystem;
  }
  return sendAuthenticatedRequest('AircraftTrackingService', 'getReceiverHeatmap', requestData, token);
}

/**
 * Get list of aircraft with predictions for a date and airport.
 */
export async function getPredictionsForDate(airportIcao, date, token, opts = {}) {
  const requestData = { airportIcao };
  if (date) requestData.date = date;
  // rangeFilter: 'NEAR' | 'LONG_RANGE' | 'ALL' (default ALL on server-side null)
  if (opts.rangeFilter) requestData.rangeFilter = opts.rangeFilter;
  if (opts.nearReceiverKm) requestData.nearReceiverKm = opts.nearReceiverKm;
  return sendAuthenticatedRequest('AircraftTrackingService', 'getPredictionsForDate', requestData, token);
}

/**
 * Get full prediction history (events) for one flight on a specific date.
 * If callsign is provided, only events for that specific flight (icao + callsign) are returned.
 */
export async function getPredictionHistory(icaoAddress, callsign, airportIcao, date, token, sessionStart, sessionEnd) {
  const requestData = { icaoAddress, airportIcao };
  if (callsign) requestData.callsign = callsign;
  if (date) requestData.date = date;
  if (sessionStart) requestData.sessionStart = sessionStart;
  if (sessionEnd) requestData.sessionEnd = sessionEnd;
  return sendAuthenticatedRequest('AircraftTrackingService', 'getPredictionHistory', requestData, token);
}

/**
 * Get list of departure sessions (engines on -> liftoff) with their latest
 * ETT forecast for the given date and airport.
 */
export async function getDeparturesForDate(airportIcao, date, token) {
  const requestData = { airportIcao };
  if (date) requestData.date = date;
  return sendAuthenticatedRequest('AircraftTrackingService', 'getDeparturesForDate', requestData, token);
}

/**
 * Get full forecast history (FORECAST + LIFTOFF events) for one departure
 * session, identified by (icao, airport, sessionStartAt).
 */
export async function getDepartureHistory(icaoAddress, airportIcao, sessionStartAt, token) {
  const requestData = { icaoAddress, airportIcao, sessionStartAt };
  return sendAuthenticatedRequest('AircraftTrackingService', 'getDepartureHistory', requestData, token);
}

/**
 * Get prediction accuracy heatmap for an airport.
 * Returns sectors with mean absolute error per 15 deg x 50km segment.
 */
export async function getPredictionAccuracyHeatmap(airportIcao, units, modelVersion, token) {
  const requestData = { airportIcao };
  if (units) requestData.units = units;
  if (modelVersion) requestData.modelVersion = modelVersion;
  return sendAuthenticatedRequest('AircraftTrackingService', 'getPredictionAccuracyHeatmap', requestData, token);
}

/**
 * Clear the prediction accuracy heatmap (records a reset cutoff).
 * Non-destructive — prediction_event history is preserved.
 */
export async function clearPredictionAccuracyHeatmap(airportIcao, modelVersion, token) {
  const requestData = { airportIcao };
  if (modelVersion) requestData.modelVersion = modelVersion;
  return sendAuthenticatedRequest('AircraftTrackingService', 'clearPredictionAccuracyHeatmap', requestData, token);
}

/**
 * Daily prediction-quality time series for a given airport/model.
 * Returns MedAE (headline) + per-band breakdowns + bias + skill score per day.
 */
export async function getPredictionQualityHistory(airportIcao, modelVersion, days, token, granularity) {
  const requestData = { airportIcao };
  if (modelVersion) requestData.modelVersion = modelVersion;
  if (days) requestData.days = days;
  if (granularity) requestData.granularity = granularity;
  return sendAuthenticatedRequest('AircraftTrackingService', 'getPredictionQualityHistory', requestData, token);
}

// ============================================================================
// Prediction Stats Service — accuracy by distance ring
//
// Distinct from getPredictionQualityHistory above, which bands by time-to-go
// (<60 min, 10–30, 30–60) over whole days. These band by DISTANCE RING, take one
// observation per flight per ring, and go down to a two-hour window — which is
// what makes the long-range estimator and its regime corrector observable.
// ============================================================================

/**
 * Accuracy per distance ring for flights that landed in a trailing window.
 * @param {string} airportIcao
 * @param {number} windowHours - 2, 8 or 24. Backend snaps anything else.
 * @param {string|null} modelVersion - null for the airport's active model.
 * @param {string} token
 */
export async function getRingAccuracy(airportIcao, windowHours, modelVersion, token) {
  const requestData = { airportIcao };
  if (windowHours) requestData.windowHours = windowHours;
  if (modelVersion) requestData.modelVersion = modelVersion;
  return sendAuthenticatedRequest('PredictionStatsService', 'getRingAccuracy', requestData, token);
}

/** Daily per-ring accuracy over a longer period (1–90 days). */
export async function getRingTrend(airportIcao, days, modelVersion, token) {
  const requestData = { airportIcao };
  if (days) requestData.days = days;
  if (modelVersion) requestData.modelVersion = modelVersion;
  return sendAuthenticatedRequest('PredictionStatsService', 'getRingTrend', requestData, token);
}

/**
 * Our arrival prediction against a commercial comparator's, at matched vintages.
 *
 * @param {string} airportIcao - EIDW only; other airports return an empty benchmark.
 * @param {number} windowHours - 24, 72 or 168. Backend snaps anything else.
 * @param {string} token
 */
export async function getComparatorBenchmark(airportIcao, windowHours, token) {
  const requestData = { airportIcao };
  if (windowHours) requestData.windowHours = windowHours;
  return sendAuthenticatedRequest('PredictionStatsService', 'getComparatorBenchmark', requestData, token);
}

/**
 * Take-off time (taxi-out) accuracy at fixed points before take-off.
 * @param {string} airportIcao
 * @param {number} days - trailing window, 1-200 (default 14)
 * @param {string} token
 */
export async function getDepartureAccuracy(airportIcao, days, token) {
  return sendAuthenticatedRequest('PredictionStatsService', 'getDepartureAccuracy',
    { airportIcao, days }, token);
}

/** Time series of the long-range regime corrector: offset, spread, flight count. */
export async function getRegimeHistory(airportIcao, hours, token) {
  const requestData = { airportIcao };
  if (hours) requestData.hours = hours;
  return sendAuthenticatedRequest('PredictionStatsService', 'getRegimeHistory', requestData, token);
}

/** Nightly calibration metrics (CRPS, interval coverage, PIT) per model and distance band. */
export async function getPredictionValidation(airportIcao, days, modelVersion, token) {
  const requestData = { airportIcao };
  if (days) requestData.days = days;
  if (modelVersion) requestData.modelVersion = modelVersion;
  return sendAuthenticatedRequest('PredictionStatsService', 'getPredictionValidation', requestData, token);
}

// ============================================================================
// Registration Service
// ============================================================================

/**
 * Update user profile
 * @param {object} profileChanges - Profile fields to update
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Update result
 */
export async function updateProfile(profileChanges, token) {
  return sendAuthenticatedRequest('RegistrationService', 'updateProfile', profileChanges, token);
}

// ============================================================================
// Receiver Management Service
// ============================================================================

/**
 * Add a new receiver
 * @param {object} receiverData - Receiver configuration
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Created receiver data
 */
export async function addReceiver(receiverData, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'addReceiver', receiverData, token);
}

/**
 * Read pending-registration info for a magic-link claim token. Used to
 * render the /claim/:token confirmation page. Unauthenticated — the token
 * itself is the auth.
 */
export async function getReceiverClaimInfo(claimToken) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'getClaimInfo',
    { claimToken }, null);
}

/**
 * Complete a receiver claim. Aviotrack is passwordless — the magic-link
 * click IS the authentication, so no password is collected at claim time.
 * New users are created with email_verified=true and no password set;
 * they can use passwordless login afterwards.
 *
 * latitude/longitude are captured on the claim page (HTTPS — geolocation
 * works here; the HTTP captive portal can't use the browser API).
 * receiverName is the friendly name shown in My Receivers; when omitted
 * the backend generates "AIRPORT - email-local-part".
 */
export async function completeReceiverClaim({ claimToken, receiverName, latitude, longitude }, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'completeClaim',
    { claimToken, receiverName, latitude, longitude }, token);
}

/**
 * Update existing receiver
 * @param {object} receiverData - Receiver configuration (must include receiverUuid)
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Update result
 */
export async function updateReceiver(receiverData, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'updateReceiver', receiverData, token);
}

/**
 * Delete a receiver
 * @param {string} receiverUuid - Receiver UUID to delete
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Delete result
 */
export async function deleteReceiver(receiverUuid, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'deleteReceiver', { receiverUuid }, token);
}

/**
 * Get viewers for a receiver
 * @param {string} receiverUuid - Receiver UUID
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Viewers list with count and maxViewers
 */
export async function getViewers(receiverUuid, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'getViewers', { receiverUuid }, token);
}

/**
 * Add a viewer to a receiver
 * @param {object} viewerData - { receiverUuid, viewerEmail, canViewHistory }
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Created viewer data
 */
export async function addViewer(viewerData, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'addViewer', viewerData, token);
}

/**
 * Remove a viewer from a receiver
 * @param {number} viewerId - Viewer record ID
 * @param {string} receiverUuid - Receiver UUID (for authorization)
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Remove result
 */
export async function removeViewer(viewerId, receiverUuid, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'removeViewer', { viewerId, receiverUuid }, token);
}

/**
 * Update viewer permissions
 * @param {object} viewerData - { viewerId, receiverUuid, canViewHistory }
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Update result
 */
export async function updateViewer(viewerData, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'updateViewer', viewerData, token);
}

// ============================================================================
// Admin Service (ADMIN role required)
// ============================================================================

/**
 * Get all users in the system (admin only)
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - All users data
 */
export async function getAllUsers(token) {
  return sendAuthenticatedRequest('UserAccountService', 'getAllUsers', {}, token);
}

/**
 * Get all receivers in the system (admin only)
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - All receivers data
 */
export async function getAllReceivers(token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'getAllReceivers', {}, token);
}

/**
 * Get recent receiver connection events (admin only)
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Recent connection events
 */
export async function getRecentConnectionEvents(token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'getRecentConnectionEvents', {}, token);
}

/**
 * Recent connection events filtered to one receiver (owner or admin).
 */
export async function getReceiverConnectionEvents(receiverUuid, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'getReceiverConnectionEvents',
    { receiverUuid }, token);
}

/**
 * Get debug buffer for a specific receiver (owner or admin)
 * @param {string} receiverUuid - Receiver UUID
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Debug buffer entries
 */
export async function getReceiverDebugBuffer(receiverUuid, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'getReceiverDebugBuffer', { receiverUuid }, token);
}

/**
 * Get all unknown (unregistered) receivers (admin only)
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Unknown receivers list with counts
 */
export async function getUnknownReceivers(token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'getUnknownReceivers', {}, token);
}

/**
 * Adopt an unknown receiver, creating a real receiver record (admin only)
 * @param {object} data - { unknownReceiverId, ownerEmail, receiverName, latitude?, longitude? }
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Adopted receiver data with UUID
 */
export async function adoptUnknownReceiver(data, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'adoptUnknownReceiver', data, token);
}

/**
 * Dismiss (block) an unknown receiver (admin only)
 * @param {object} data - { unknownReceiverId }
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Dismiss result
 */
export async function dismissUnknownReceiver(data, token) {
  return sendAuthenticatedRequest('ReceiverManagementService', 'dismissUnknownReceiver', data, token);
}

// ============================================================================
// CSV Position Debug Service
// ============================================================================

/**
 * Process CSV data through the ADS-B decode/validation pipeline (admin only)
 * @param {string} receiverUuid - Receiver UUID to simulate processing for
 * @param {string} csvData - Base64-encoded CSV data
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Debug results with per-aircraft message timeline
 */
export async function processCsvDebug(receiverUuid, csvData, token) {
  return sendAuthenticatedRequest('CsvPositionDebugService', 'processCsvDebug',
    { receiverUuid, csvData }, token);
}

// ============================================================================
// Airport View Service
// ============================================================================

/**
 * Get aircraft currently on ground at an airport
 * @param {string} airportIcao - Airport ICAO code (e.g., "EGLL", "KJFK")
 * @param {string} receiverUuid - Receiver UUID for filtering
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Ground aircraft data with status (TAXIING_IN, PARKED, ENGINES_ON, PUSHBACK, TAXIING_OUT, HOLDING)
 */
export async function getAircraftOnGround(airportIcao, receiverUuid, token) {
  return sendAuthenticatedRequest('AirportViewService', 'getAircraftOnGround', { airportIcao, receiverUuid }, token);
}

/**
 * The mobile airport board: on-ground / arrivals / departures for one airport
 * today, as current status per aircraft (~15 fields per row). Built server-side
 * from the same rows the Predictions page shows.
 * @param {string} airportIcao - Airport ICAO code
 * @param {string|null} receiverUuid - Optional; scopes isNew/isRegular on on-ground rows
 * @param {string} token - JWT access token
 */
export async function getAirportBoard(airportIcao, receiverUuid, token) {
  const requestData = { airportIcao };
  if (receiverUuid && receiverUuid !== 'ALL_RECEIVERS') requestData.receiverUuid = receiverUuid;
  return sendAuthenticatedRequest('AirportViewService', 'getAirportBoard', requestData, token);
}

/**
 * Get detailed information about a specific ground operation
 * @param {number} groundOperationId - Ground operation ID
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Ground operation details including full timeline
 */
export async function getGroundOperationDetails(groundOperationId, token) {
  return sendAuthenticatedRequest('AirportViewService', 'getGroundOperationDetails', { groundOperationId }, token);
}

/**
 * Get track points for a ground operation
 * @param {number} groundOperationId - Ground operation ID
 * @param {boolean} includeApproach - Include approach/final track before touchdown (default: true)
 * @param {boolean} includeClimb - Include initial climb track after takeoff (default: true)
 * @param {string} token - JWT access token
 * @returns {Promise<object>} - Track points with phase information
 */
export async function getGroundTrackPoints(groundOperationId, includeApproach, includeClimb, token) {
  const requestData = { groundOperationId };
  if (typeof includeApproach === 'boolean') {
    requestData.includeApproach = includeApproach;
  }
  if (typeof includeClimb === 'boolean') {
    requestData.includeClimb = includeClimb;
  }
  return sendAuthenticatedRequest('AirportViewService', 'getGroundTrackPoints', requestData, token);
}

// ========== ACARS View Service ==========

/**
 * Get ACARS messages for a receiver on a given date
 */
export async function getAcarsMessages(receiverUuid, date, token, limit = 200, address = null, interestingOnly = false) {
  const requestData = { receiverUuid, date, limit };
  if (address) requestData.address = address;
  if (interestingOnly) requestData.interestingOnly = true;
  return sendAuthenticatedRequest('AcarsViewService', 'getAcarsMessages', requestData, token);
}

/**
 * Get unique aircraft seen via ACARS for a receiver on a given date
 */
export async function getAcarsAircraft(receiverUuid, date, token) {
  return sendAuthenticatedRequest('AcarsViewService', 'getAcarsAircraft', { receiverUuid, date }, token);
}

/**
 * Get ACARS summary statistics for a receiver on a given date
 */
export async function getAcarsStats(receiverUuid, date, token) {
  return sendAuthenticatedRequest('AcarsViewService', 'getAcarsStats', { receiverUuid, date }, token);
}

// ========== Airband View Service ==========

/**
 * Get transcribed airband voice messages for a receiver on a given date
 */
export async function getAirbandTranscripts(receiverUuid, date, token, limit = 200) {
  return sendAuthenticatedRequest('AirbandViewService', 'getAirbandTranscripts', { receiverUuid, date, limit }, token);
}

/**
 * Get airband voice stats (per-channel counts, confidence, rssi) for a receiver on a given date
 */
export async function getAirbandStats(receiverUuid, date, token) {
  return sendAuthenticatedRequest('AirbandViewService', 'getAirbandStats', { receiverUuid, date }, token);
}

/**
 * Save a human label for one airband transcript (the fine-tune training set).
 * status is 'labeled' (label = corrected text) or 'ignored' (unintelligible; label may be blank).
 */
export async function saveAirbandLabel(receiverUuid, transcriptId, label, status, token) {
  return sendAuthenticatedRequest('AirbandViewService', 'saveAirbandLabel',
    { receiverUuid, transcriptId, label, status }, token);
}

// ── NOTAM API ──

/**
 * Get active NOTAMs within map bounds
 */
export async function getNotamsInBounds(bounds, token) {
  return sendAuthenticatedRequest('NotamViewService', 'getNotamsInBounds', bounds, token);
}

/**
 * Get active NOTAMs with optional filters
 */
export async function getActiveNotams(filters, token) {
  return sendAuthenticatedRequest('NotamViewService', 'getActiveNotams', filters || {}, token);
}

/**
 * Get full details for a single NOTAM
 */
export async function getNotamDetails(notamId, token) {
  return sendAuthenticatedRequest('NotamViewService', 'getNotamDetails', { notamId }, token);
}

/**
 * Admin: trigger initial NOTAM load from JMS queue
 */
export async function loadInitialNotams(timeoutSeconds, token) {
  return sendAuthenticatedRequest('NotamViewService', 'loadInitialNotams', { timeoutSeconds: timeoutSeconds || 30 }, token);
}

/**
 * Admin: get track anomalies for review
 */
export async function getTrackAnomalies(filters, token) {
  return sendAuthenticatedRequest('TrackAnomalyService', 'getTrackAnomalies', filters, token);
}

/**
 * Admin: review a track anomaly (correct/incorrect with feedback)
 */
export async function reviewTrackAnomaly(anomalyId, correct, feedback, token) {
  return sendAuthenticatedRequest('TrackAnomalyService', 'reviewTrackAnomaly', {
    anomalyId, correct, feedback
  }, token);
}

/**
 * Admin: backfill anomalies from historical track data
 */
export async function backfillAnomalies(airportIcao, daysBack, token) {
  return sendAuthenticatedRequest('TrackAnomalyService', 'backfillAnomalies', {
    airportIcao: airportIcao || null,
    daysBack: daysBack || 14
  }, token);
}

// ============================================================================
// Receiver Config Service (remote feeder config push)
// ============================================================================

/**
 * Read the desired + last-applied config for a receiver. Owner or admin.
 * @param {string} receiverUuid
 * @param {string} token
 */
export async function getReceiverConfig(receiverUuid, token) {
  return sendAuthenticatedRequest('ReceiverConfigService', 'getReceiverConfig',
    { receiverUuid }, token);
}

/**
 * Push a new desired config (full or partial JSON) to a receiver. Owner or admin.
 * Backend validates, increments desired_version, persists, and pushes
 * CFG-PUSH on the receiver's TCP socket if it's currently connected.
 * @param {string} receiverUuid
 * @param {object} configPatch - object with the keys to set (subset of schema)
 * @param {string} token
 */
export async function updateReceiverConfig(receiverUuid, configPatch, token) {
  return sendAuthenticatedRequest('ReceiverConfigService', 'updateReceiverConfig', {
    receiverUuid,
    configJson: JSON.stringify(configPatch || {})
  }, token);
}

/**
 * Push a software update to a receiver. Owner or admin. The backend signals
 * the receiver (if connected) to download the latest release and restart;
 * returns { pushed: boolean } indicating whether the receiver was reached.
 * @param {string} receiverUuid
 * @param {string} token
 */
export async function pushReceiverUpdate(receiverUuid, token) {
  return sendAuthenticatedRequest('ReceiverConfigService', 'pushUpdate',
    { receiverUuid }, token);
}

/**
 * Turn the SERVER-side auto-update opt-in on or off for one receiver.
 *
 * This is not the receiver's own local autoUpdate flag (that one lives in the device's
 * update.properties and a headless Pi has no UI to reach it). When enabled, a scheduled backend job
 * pushes UPDATE whenever that receiver's own platform advertises a newer build.
 * @param {string} receiverUuid
 * @param {boolean} autoUpdate
 * @param {string} token
 */
export async function setReceiverAutoUpdate(receiverUuid, autoUpdate, token) {
  return sendAuthenticatedRequest('ReceiverConfigService', 'setAutoUpdate',
    { receiverUuid, autoUpdate }, token);
}

/**
 * Schema metadata for the config form (label, type, range, requiresRestart…).
 * Cache-friendly — does not depend on the receiver.
 * @param {string} token
 */
export async function getReceiverConfigSchema(token) {
  return sendAuthenticatedRequest('ReceiverConfigService', 'getReceiverConfigSchema', {}, token);
}

// ============================================================================
// Receiver Stats Service (graphs1090-style time series)
// ============================================================================

/**
 * Down-sampled time series for one receiver. Owner or admin.
 * @param {string} receiverUuid
 * @param {string} fromTs       - ISO-8601 instant; default now-1h
 * @param {string} toTs         - ISO-8601 instant; default now
 * @param {number} stepSeconds  - 0 = backend picks per range
 * @param {string[]} metrics    - optional subset; null = default set
 * @param {string} token
 */
export async function getReceiverStats(receiverUuid, fromTs, toTs, stepSeconds, metrics, token) {
  return sendAuthenticatedRequest('ReceiverStatsService', 'getReceiverStats', {
    receiverUuid,
    fromTs: fromTs || null,
    toTs: toTs || null,
    stepSeconds: stepSeconds || 0,
    metrics: metrics || null
  }, token);
}

/**
 * Single most-recent stats row for a receiver. Cheap; powers the live header strip.
 */
export async function getReceiverStatsLatest(receiverUuid, token) {
  return sendAuthenticatedRequest('ReceiverStatsService', 'getReceiverStatsLatest',
    { receiverUuid }, token);
}

/**
 * Batched STATS-derived metadata for a list of receiver UUIDs.
 * Returns { byUuid: { uuid: { deviceType, software, softwareVersion, lastStatsAtMs } } }.
 * UUIDs without recent STATS are absent from the map.
 */
export async function getReceiversMetadata(receiverUuids, token) {
  return sendAuthenticatedRequest('ReceiverStatsService', 'getReceiversMetadata',
    { receiverUuids }, token);
}

// ---- Statistics page (per-receiver source-tabbed dashboard) ----

/**
 * Which data-source tabs (ADS-B/ACARS/Airband) to show for a receiver.
 * Returns { adsb, acars, airband, defaultTab }. Inferred from data presence.
 */
export async function getReceiverCapabilities(receiverUuid, token) {
  return sendAuthenticatedRequest('ReceiverStatsService', 'getReceiverCapabilities',
    { receiverUuid }, token);
}

/**
 * Site-information header for the Statistics page: identity, location,
 * all-time totals, peak aircraft/range, and active-day streaks.
 */
export async function getReceiverSiteOverview(receiverUuid, token) {
  return sendAuthenticatedRequest('ReceiverStatsService', 'getReceiverSiteOverview',
    { receiverUuid }, token);
}

/**
 * Per-day metric breakdown for one source ("adsb"|"acars").
 * Returns { buckets: [{ date, metrics: {..} }] }.
 */
export async function getReceiverDailyBreakdown(receiverUuid, source, days, token) {
  return sendAuthenticatedRequest('ReceiverStatsService', 'getReceiverDailyBreakdown',
    { receiverUuid, source, days: days || 0 }, token);
}

/**
 * Per-hour (UTC) metric breakdown for one source on a single day.
 * Returns { date, buckets: [{ hour, metrics: {..} }] }.
 */
export async function getReceiverHourlyBreakdown(receiverUuid, source, date, token) {
  return sendAuthenticatedRequest('ReceiverStatsService', 'getReceiverHourlyBreakdown',
    { receiverUuid, source, date: date || null }, token);
}

/**
 * ADS-B positions per (day, hour) over the last N days — the report-heatmap grid.
 * Returns { days, maxPositions, cells: [{ date, hour, positions }] }. Heavier
 * Greptime query — fetch lazily / separately from the rest of the tab.
 */
export async function getReceiverReportHeatmap(receiverUuid, days, token) {
  return sendAuthenticatedRequest('ReceiverStatsService', 'getReceiverReportHeatmap',
    { receiverUuid, days: days || 0 }, token);
}

/**
 * Fetch the admin-assigned aircraft type → sprite cell mapping. Called at
 * boot so the FE renders newly-assigned icons without a redeploy.
 */
export async function getTypeIconMap(token) {
  return sendAuthenticatedRequest('AircraftDataService', 'getTypeIconMap', {}, token);
}

/**
 * Public, alias-resolved airline ICAO → logo URL map. Called at boot (before
 * login) so the FE never needs a redeploy to pick up a new airline logo.
 * @returns {Promise<{logos: Object<string,string>, count: number, generatedAt: string}>}
 */
export async function getAirlineLogoMap() {
  return sendPublicRequest('AircraftDataService', 'getAirlineLogoMap', {});
}

// ─────────────────────────────────────────────────────────────────────────────
// AdminDataService — aircraft + airline reference-data management.
// ─────────────────────────────────────────────────────────────────────────────

export async function getIncompleteAircraft(filters, token) {
  return sendAuthenticatedRequest('AdminDataService', 'getIncompleteAircraft',
    filters || {}, token);
}

export async function updateAircraft(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'updateAircraft', payload, token);
}

export async function getAirlineGaps(filters, token) {
  return sendAuthenticatedRequest('AdminDataService', 'getAirlineGaps',
    filters || {}, token);
}

export async function upsertAirline(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'upsertAirline', payload, token);
}

export async function uploadAirlineLogo(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'uploadAirlineLogo', payload, token);
}

export async function getAircraftTypeIcons(filters, token) {
  return sendAuthenticatedRequest('AdminDataService', 'getAircraftTypeIcons',
    filters || {}, token);
}

export async function assignAircraftTypeIcon(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'assignAircraftTypeIcon', payload, token);
}

export async function assignAircraftTypeIconBulk(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'assignAircraftTypeIconBulk', payload, token);
}

export async function enrichMissingAirlines(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'enrichMissingAirlines', payload || {}, token);
}

export async function getPendingAirlines(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'getPendingAirlines', payload || {}, token);
}

export async function approveAirlineSuggestion(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'approveAirlineSuggestion', payload, token);
}

export async function rejectAirlineSuggestion(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'rejectAirlineSuggestion', payload, token);
}

export async function approveHighConfidence(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'approveHighConfidence', payload || {}, token);
}

/**
 * Aircraft missing a type, with their cached Planespotters photo (the URL slug
 * names the type) and a registry-derived suggestion where one exists.
 * @param {{limit?: number, recentDays?: number, fetchMissing?: boolean, maxFetch?: number, includeNotFound?: boolean}} payload
 */
export async function getUnresolvedAircraftPhotos(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'getUnresolvedAircraftPhotos', payload || {}, token);
}

/**
 * Write resolved type designators against aircraft hexes.
 * @param {{resolutions: Array<{icao24: string, typeDesignator: string, manufacturer?: string, model?: string, source?: string, note?: string}>, dryRun?: boolean, overwrite?: boolean, fillFromType?: boolean}} payload
 */
export async function applyAircraftTypeResolutions(payload, token) {
  return sendAuthenticatedRequest('AdminDataService', 'applyAircraftTypeResolutions', payload, token);
}

// ─────────────────────────────────────────────────────────────────────────────
// ReferenceDataService — generic CRUD over the icao_* reference tables plus
// the ICAOList (github.com/rikgale/ICAOList) sync. Rows are objects keyed by
// snake_case DB column names with string values.
// ─────────────────────────────────────────────────────────────────────────────

export async function getReferenceTables(token) {
  return sendAuthenticatedRequest('ReferenceDataService', 'getReferenceTables', {}, token);
}

/**
 * @param {{table: string, q?: string, filters?: object, sortBy?: string, sortDir?: string, page?: number, pageSize?: number}} payload
 *        page is 0-based. Response: {table, rows, page, pageSize, totalCount} — totalCount -1 = unknown.
 */
export async function listReferenceRows(payload, token) {
  return sendAuthenticatedRequest('ReferenceDataService', 'listReferenceRows', payload, token);
}

/**
 * @param {{table: string, key?: string, values: object, lock?: boolean}} payload
 *        Composite keys are joined with '|' (hex_start|hex_end, country|prefix).
 */
export async function upsertReferenceRow(payload, token) {
  return sendAuthenticatedRequest('ReferenceDataService', 'upsertReferenceRow', payload, token);
}

/** @param {{table: string, key: string, confirm?: boolean}} payload */
export async function deleteReferenceRow(payload, token) {
  return sendAuthenticatedRequest('ReferenceDataService', 'deleteReferenceRow', payload, token);
}

/** @param {{datasets?: string[], sampleSize?: number}} payload */
export async function previewIcaoListSync(payload, token) {
  return sendAuthenticatedRequest('ReferenceDataService', 'previewIcaoListSync', payload || {}, token);
}

/** @param {{datasets?: string[], allowInserts?: boolean, allowUpdates?: boolean, expectedSha256?: object}} payload */
export async function applyIcaoListSync(payload, token) {
  return sendAuthenticatedRequest('ReferenceDataService', 'applyIcaoListSync', payload, token);
}

/** @param {{dataset?: string, limit?: number}} payload */
export async function getSyncHistory(payload, token) {
  return sendAuthenticatedRequest('ReferenceDataService', 'getSyncHistory', payload || {}, token);
}

// ---- Geospatial Configurator (billing GeospatialService) ----

export async function getGeospatialFeatures(airportIcao, featureType, token) {
  return sendAuthenticatedRequest('GeospatialService', 'getAirportFeatures',
    { airportIcao, featureType }, token);
}

export async function saveGeospatialFeature(payload, token) {
  return sendAuthenticatedRequest('GeospatialService', 'saveAirportFeature', payload, token);
}

export async function deleteGeospatialFeature(airportIcao, id, token) {
  return sendAuthenticatedRequest('GeospatialService', 'deleteAirportFeature',
    { airportIcao, id }, token);
}

// ---- Billing dashboard (billing BillingDashboardService) ----

export async function getBillingDashboard(payload, token) {
  return sendAuthenticatedRequest('BillingDashboardService', 'getBillingDashboard',
    payload, token);
}

export async function getMovementDetail(airportIcao, movementId, token) {
  return sendAuthenticatedRequest('BillingDashboardService', 'getMovementDetail',
    { airportIcao, movementId }, token);
}

export async function getBillingAircraft(payload, token) {
  return sendAuthenticatedRequest('BillingDashboardService', 'getBillingAircraft',
    payload, token);
}

export async function getMovementTrack(airportIcao, movementId, token) {
  return sendAuthenticatedRequest('BillingDashboardService', 'getMovementTrack',
    { airportIcao, movementId }, token);
}

// ---- Billing rules (admin) ----

export async function getBillingRules(payload, token) {
  return sendAuthenticatedRequest('BillingService', 'getBillingRules',
    payload, token);
}

export async function saveBillingRule(payload, token) {
  return sendAuthenticatedRequest('BillingService', 'saveBillingRule',
    payload, token);
}

export async function deleteBillingRule(payload, token) {
  return sendAuthenticatedRequest('BillingService', 'deleteBillingRule',
    payload, token);
}
